import { useGeocoding } from 'hooks/maps.hooks';
import merge from 'lodash.merge';
import { useEffect, useRef, useState } from 'react';

const types = ['address', 'place', 'postcode'];

export const useMapboxGeocoding = (options = {}) => {
  const optionsRef = useRef(
    merge(
      {
        delay: 750,
        suggestOptions: {
          limit: 5,
          types,
          proximity: 'ip',
        },
      },
      options,
    ),
  );
  const { delay, suggestOptions } = optionsRef.current;

  const geocodingCore = useGeocoding();
  const [searchText, setSearchText] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    const handler = setTimeout(async () => {
      if (!searchText) {
        setSuggestions([]);
        return;
      }
      const results = await geocodingCore.forward(searchText, suggestOptions);
      const mappedSuggestions = results.features.map(feature => ({
        place_id: feature.id,
        address: feature.properties.name_preferred,
        place_formatted: feature.properties.place_formatted,
        description: feature.properties.full_address,
        full_address: feature.properties.full_address,
      }));
      setSuggestions(mappedSuggestions);
    }, delay);

    return () => clearTimeout(handler);
  }, [delay, searchText, geocodingCore, suggestOptions]);

  const fetchPermanentAddress = async fullAddress => {
    const results = await geocodingCore.forward(fullAddress, {
      limit: 1,
      types,
      permanent: true,
    });

    return geocodingToAddress(results.features[0]);
  };

  return {
    setSearchText,
    suggestions,
    fetchPermanentAddress,
  };
};

const geocodingToAddress = geocodingResponse => {
  return {
    formatted_address: geocodingResponse.properties.place_formatted,
    lat: geocodingResponse.properties.coordinates?.latitude,
    lon: geocodingResponse.properties.coordinates?.longitude,
    street1: geocodingResponse.properties.context.address?.address_number,
    street2: geocodingResponse.properties.context.street?.name,
    city: geocodingResponse.properties.context.place?.name,
    state: geocodingResponse.properties.context.region?.name,
    country: geocodingResponse.properties.context.country?.name,
    postalCode: geocodingResponse.properties.context.postcode?.name,
  };
};
